import { Dispatch, SetStateAction, useEffect, useState, useImperativeHandle, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import SupportedDtoIn from '@/models/in/SupportedDtoIn';
import SupportedNameFilter from './filters/SupportedNameFilter';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import styles from '@/components/filters/FiltersArea.module.scss';
import ExpirationStatusFilters from '@/components/filters/ExpirationStatusFilters/ExpirationStatusFilters';
import { ExpirationStatus, getExpirationStatus } from '@/helpers/contactClassificationExpiration';
import IsraeliIdChangeFilter, { IsraeliIdChangeFilterProps } from './IsraeliIdChange/IsraeliIdChangeFilter';

interface FiltersAreaProps {
  supporteds: SupportedDtoIn[];
  setFilteredSupporteds: Dispatch<SetStateAction<SupportedDtoIn[]>>;
  selectedSupportedName: string | null;
  setSelectedSupportedName: (supportedName: string | null) => void;
  ref?: Ref<{ resetFilters: () => void }>;
  /** HAT-1428: the supporteds with an open ID correction request - the page filters by it. */
  idChangeFilter?: IsraeliIdChangeFilterProps;
}

export interface FilterState {
  selectedClassifications: string[];
  selectedExpiration: ExpirationStatus | null;
}

const NO_CLASSIFICATION = 'NO_CLASSIFICATION';

const matchesSupportedName = (supported: SupportedDtoIn, supportedName: string | null): boolean => {
  return !supportedName || supported.firstName + ' ' + supported.lastName === supportedName;
};

const matchesClassification = (supported: SupportedDtoIn, selectedClassifications: string[]): boolean => {
  if (selectedClassifications.length === 0) return true;

  const hasNone = (supported.contactClassifications ?? []).length === 0;
  if (hasNone) return selectedClassifications.includes(NO_CLASSIFICATION);

  return (supported.contactClassifications ?? []).some((contactClassification) => selectedClassifications.includes(contactClassification.classification.name));
};

const matchesExpiration = (supported: SupportedDtoIn, selectedExpiration: ExpirationStatus | null): boolean => {
  if (!selectedExpiration) {
    return true;
  }

  return (supported.contactClassifications ?? []).some((contactClassification) => {
    return getExpirationStatus(contactClassification) === selectedExpiration;
  });
};

/**
 * `?expiration=ExpiringSoon` - "תמונת מצב" links here from "דורש טיפול", and without it the link
 * landed on every supported instead of the ones it counted. It only sets the starting chip, which
 * shows as selected and clears like any other.
 */
const parseExpiration = (value: string | null): ExpirationStatus | null =>
  value && (Object.values(ExpirationStatus) as string[]).includes(value) ? (value as ExpirationStatus) : null;

const FiltersArea = ({ supporteds, setFilteredSupporteds, selectedSupportedName, setSelectedSupportedName, ref, idChangeFilter }: FiltersAreaProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [filterState, setFilterState] = useState<FilterState>(() => ({
    selectedClassifications: [],
    selectedExpiration: parseExpiration(searchParams.get('expiration')),
  }));

  const classificationCounts: { [type: string]: number } = {};

  supporteds.forEach((supported) => {
    if ((supported.contactClassifications ?? []).length === 0) {
      classificationCounts[NO_CLASSIFICATION] = (classificationCounts[NO_CLASSIFICATION] || 0) + 1;
    } else {
      (supported.contactClassifications ?? []).forEach((contactClassification) => {
        const name = contactClassification.classification.name;
        classificationCounts[name] = (classificationCounts[name] || 0) + 1;
      });
    }
  });

  const existingClassifications = Object.keys(classificationCounts);

  const resetAllFilters = () => {
    setFilterState({
      selectedClassifications: [],
      selectedExpiration: null,
    });

    setSelectedSupportedName(null);
  };

  const handleClassificationChange = (type: string) => {
    const isClassificationSelected = filterState.selectedClassifications.includes(type);

    setFilterState({
      ...filterState,
      selectedClassifications: isClassificationSelected
        ? filterState.selectedClassifications.filter((c) => c !== type)
        : [...filterState.selectedClassifications, type],
    });
  };

  const expirationCounts: { [key: string]: number } = {};

  supporteds.forEach((supported) => {
    (supported.contactClassifications ?? []).forEach((contactClassification) => {
      const expirationStatus: ExpirationStatus | null = getExpirationStatus(contactClassification);
      if (expirationStatus) {
        expirationCounts[expirationStatus] = (expirationCounts[expirationStatus] || 0) + 1;
      }
    });
  });

  const handleExpirationToggle = (expirationStatus: ExpirationStatus) => {
    const isSelected = filterState.selectedExpiration === expirationStatus;

    setFilterState({
      ...filterState,
      selectedExpiration: isSelected ? null : expirationStatus,
    });
  };

  useEffect(() => {
    const filtered = supporteds.filter((supported) => {
      return (
        matchesSupportedName(supported, selectedSupportedName) &&
        matchesClassification(supported, filterState.selectedClassifications) &&
        matchesExpiration(supported, filterState.selectedExpiration)
      );
    });

    setFilteredSupporteds(filtered);
  }, [filterState, selectedSupportedName, supporteds, setFilteredSupporteds]);

  useImperativeHandle(ref, () => ({
    resetFilters: resetAllFilters,
  }));

  return (
    <div className="flex flex-col gap-5">
      <ExpirationStatusFilters counts={expirationCounts} selectedExpiration={filterState.selectedExpiration} onToggle={handleExpirationToggle} />

      <div className={styles.filtersArea}>
        <div className={styles.filtersContainer} data-board-filters>
          {idChangeFilter && <IsraeliIdChangeFilter {...idChangeFilter} />}
          {existingClassifications.map((type) => (
            <StatusFilter
              key={type}
              title={type === NO_CLASSIFICATION ? t('noClassification') : type}
              isSelected={filterState.selectedClassifications.includes(type)}
              onClick={() => handleClassificationChange(type)}
              count={classificationCounts[type] || 0}
            />
          ))}

          <SupportedNameFilter selectedSupportedName={selectedSupportedName} setSelectedSupportedName={setSelectedSupportedName} />
        </div>
      </div>
    </div>
  );
};

export default FiltersArea;
